import React, { useEffect } from 'react';
import { PageContentWrapper, LeftWrapper, MainPanel } from './BunkeringPage.styles';
import BunkeringDateListContainer from 'src/components/bunkering/bunkering-date-list/BunkeringDateListContainer';
import BunkeringContainer from 'src/components/bunkering/BunkeringContainer';
import { useAolStore } from 'src/context/AolContext';
import { intitializeDateFilter } from 'src/utils/dateFilterUtils';
import { useUserSettingsStore } from 'src/context/UserSettingsContext';
import { useAuthStore } from 'src/context/AuthContext';
import useGetAolData from 'src/api/aol/useGetAolData';
import { useVesselStore } from 'src/context/VesselContext';
import { formatAolUrlDate } from 'src/utils/aol/aolDateUtils';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import useGetVessel from 'src/api/useGetVessel';
import { useBunkerContext } from 'src/context/BunkerContext';
import { getThreeYearsBefore } from 'src/components/bunkering/BunkeringUtils';
import useApiLoaderHelper from 'src/utils/useApiLoaderHelper';
import { useStowageStore } from 'src/context/StowageContext';
import useCheckStowageIntegration from 'src/app/useCheckStowageIntegration';

const BunkeringPage: React.FC = () => {
    const { aolFilter, setAolFilter, setDefaultAolFilter, setPeriod, aolDataItems } = useAolStore();
    const { selectedVessel } = useVesselStore();
    const { userSettings, userSettingsLoaded } = useUserSettingsStore();
    const { bunkeringEvents, bunkerings } = useBunkerContext();
    const { role } = useAuthStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { getAolData } = useGetAolData();
    const { getBunkeringEvents, getBunkerings, getFuelRobData, getBunkerLossData } = useGetBunkeringData();
    const { getVessel } = useGetVessel();
    const { isLoadingType } = useApiLoaderHelper();
    const { checkLoadedStowageIntegration } = useCheckStowageIntegration();

    const isLoadingAols = isLoadingType('loading_aols');

    useEffect(() => {
        if (!aolFilter && userSettingsLoaded && role) {
            intitializeDateFilter(userSettings, aolFilter, setAolFilter, setDefaultAolFilter, setPeriod, role);
        }
    }, [aolFilter, userSettingsLoaded, role]);

    useEffect(() => {
        if (aolFilter && selectedVessel && !checkLoadedStowageIntegration(selectedVessel.imoNumber, loadedMasterStowagePlan)) {
            getAolData(selectedVessel.imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate));
            getBunkeringEvents(
                selectedVessel.imoNumber,
                formatAolUrlDate(getThreeYearsBefore(aolFilter.fromDate)),
                formatAolUrlDate(aolFilter.toDate),
                false
            );
        }
    }, [aolFilter, selectedVessel, loadedMasterStowagePlan]);

    useEffect(() => {
        if (bunkeringEvents && aolFilter && selectedVessel && bunkeringEvents.length > 0) {
            getBunkerings(
                selectedVessel.imoNumber,
                formatAolUrlDate(getThreeYearsBefore(aolFilter.fromDate)),
                formatAolUrlDate(aolFilter.toDate),
                false
            );
        }
    }, [bunkeringEvents]);

    useEffect(() => {
        const aolDataItemsArray = aolDataItems || [];
        const bunkeringsArray = bunkerings || [];

        if (!isLoadingAols && bunkeringsArray.length > 0 && aolDataItemsArray.length > 0) {
            const noonReportIds = aolDataItemsArray.map((item) => item.noonReport?.metadata?.noonReportId).filter((id) => !!id);
            getFuelRobData(
                selectedVessel.imoNumber,
                noonReportIds,
                bunkeringsArray.map((bunkering) => bunkering.bunkeringGuid)
            );
            getBunkerLossData(noonReportIds, selectedVessel.imoNumber);
        }
    }, [bunkerings, aolDataItems, isLoadingAols]);

    useEffect(() => {
        if (selectedVessel) {
            getVessel(selectedVessel.imoNumber);
        }
    }, [selectedVessel]);

    return (
        <PageContentWrapper>
            <LeftWrapper>
                <BunkeringDateListContainer />
            </LeftWrapper>
            <MainPanel>
                <BunkeringContainer />
            </MainPanel>
        </PageContentWrapper>
    );
};

export default BunkeringPage;
